<template>
  <vs-row
    :vs-justify="description ? 'space-between' : 'center'"
    vs-type="flex"
    vs-w="12"
  >
    <vs-col
      v-if="description"
      class="vs-pagination--mb"
      vs-type="flex"
      vs-justify="flex-start"
      vs-align="center"
      vs-lg="6"
      vs-sm="12"
      vs-xs="12"
    >
      <div>
        <span style="margin-right: 5px">
          {{ descriptionTitle }}: {{ minRows }} - {{ maxRows }}
          {{ descriptionConnector }} {{ sizeArray }} | {{ descriptionBody }}:
        </span>
        <ul class="vs-pagination--array">
          <li v-for="(row, index) in descriptionItems" :key="index">
            <span
              :class="[
                `vs-description-${color}`,
                { 'vs-pagination--bold': index === indexRows },
              ]"
              :style="descriptionStyle"
              @click="changeRowMaxItems(index)"
            >
              {{ row }}
            </span>
            <span v-if="index !== descriptionItems.length - 1"> , </span>
          </li>
        </ul>
      </div>
    </vs-col>
    <vs-col
      class="vs-pagination--mb"
      :vs-lg="description ? 6 : 12"
      vs-type="flex"
      vs-justify="flex-end"
      vs-align="center"
      vs-sm="12"
      vs-xs="12"
    >
      <div
        class="con-vs-pagination"
        :class="`vs-pagination-${color}`"
        :style="paginationStyle"
      >
        <nav class="vs-pagination--nav">
          <button
            class="vs-pagination--buttons btn-prev-pagination vs-pagination--button-prev"
            :class="{ disabled: current <= 1 }"
            :disabled="current === 1"
            @click="prevPage"
          >
            <vs-icon
              :icon-pack="iconPack"
              :icon="prevIcon || defaultPrevIcon"
            />
          </button>
          <ul class="vs-pagination--ul">
            <li
              v-for="(page, index) in pages"
              :key="index"
              class="item-pagination vs-pagination--li"
              :class="{ 'is-current': page === current }"
              @click="goTo(page)"
            >
              <span>{{ page }}</span>
              <div class="effect" />
            </li>
          </ul>
          <button
            class="vs-pagination--buttons btn-next-pagination vs-pagination--button-next"
            :class="{ disabled: current === total }"
            :disabled="current === total"
            @click="nextPage"
          >
            <vs-icon
              :icon-pack="iconPack"
              :icon="nextIcon || defaultNextIcon"
            />
          </button>
          <input
            v-if="goto"
            v-model="go"
            class="vs-pagination--input-goto"
            :max="total"
            min="1"
            type="number"
            @change="goTo"
          />
        </nav>
      </div>
    </vs-col>
  </vs-row>
</template>

<script>
const THEME_COLORS = [
  "primary",
  "secondary",
  "success",
  "danger",
  "warning",
  "dark",
  "light",
];

function getColor(color, opacity = 1) {
  if (!color || opacity === 1) return color;

  if (/^#/.test(color)) {
    const hex = color.replace("#", "");
    const normalized =
      hex.length === 3
        ? hex
            .split("")
            .map((character) => character + character)
            .join("")
        : hex;
    const value = parseInt(normalized, 16);
    if (Number.isNaN(value)) return color;
    return `rgba(${(value >> 16) & 255},${(value >> 8) & 255},${
      value & 255
    },${opacity})`;
  }

  if (/^rgb\(/.test(color)) {
    return color.replace(/^rgb\(/, "rgba(").replace(/\)$/, `,${opacity})`);
  }

  return color;
}

export default {
  name: "VsPagination",
  props: {
    modelValue: {
      type: Number,
      default: undefined,
    },
    value: {
      type: Number,
      default: 1,
    },
    color: {
      type: String,
      default: "primary",
    },
    total: {
      type: Number,
      required: true,
    },
    sizeArray: {
      type: Number,
      default: undefined,
    },
    maxItems: {
      type: [Number, String],
      default: undefined,
    },
    max: {
      type: Number,
      default: 9,
    },
    goto: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: undefined,
    },
    prevIcon: {
      type: String,
      default: undefined,
    },
    nextIcon: {
      type: String,
      default: undefined,
    },
    iconPack: {
      type: String,
      default: "material-icons",
    },
    description: {
      type: Boolean,
      default: false,
    },
    descriptionItems: {
      type: Array,
      default: () => [],
    },
    descriptionTitle: {
      type: String,
      default: "Registries",
    },
    descriptionConnector: {
      type: String,
      default: "of",
    },
    descriptionBody: {
      type: String,
      default: "Pages",
    },
  },
  emits: [
    "update:modelValue",
    "input",
    "change",
    "changeMaxItems",
  ],
  data() {
    return {
      pages: [],
      current: 0,
      go: 0,
      prevRange: "",
      nextRange: "",
      minRows: 0,
      maxRows: 0,
      indexRows: 0,
    };
  },
  computed: {
    selectedValue() {
      return this.modelValue === undefined ? this.value : this.modelValue;
    },
    isRtl() {
      return Boolean(this.$vs && this.$vs.rtl);
    },
    defaultNextIcon() {
      return this.isRtl ? "chevron_left" : "chevron_right";
    },
    defaultPrevIcon() {
      return this.isRtl ? "chevron_right" : "chevron_left";
    },
    paginationStyle() {
      if (THEME_COLORS.includes(this.color)) return {};
      return {
        "--vs-color-pagination": getColor(this.color),
        "--vs-color-pagination-alpha": getColor(this.color, 0.5),
      };
    },
    descriptionStyle() {
      return {
        cursor: "pointer",
      };
    },
  },
  watch: {
    current(value) {
      this.getPages();
      this.calculateMinMax(value);
      this.$emit("update:modelValue", value);
      this.$emit("input", value);
      this.$emit("change", value);
    },
    total() {
      this.getPages();
    },
    max() {
      this.getPages();
    },
    selectedValue(value) {
      const page = value < 1 ? 1 : value <= this.total ? value : this.total;
      this.goTo(page);
    },
  },
  mounted() {
    this.current = this.go = this.selectedValue;
    this.calculateMinMax(this.current);
    this.indexRows = this.descriptionItems.indexOf(this.maxItems);
    this.getPages();
  },
  methods: {
    changeRowMaxItems(index) {
      this.indexRows = index;
      this.$emit("changeMaxItems", index);
      this.calculateMinMax(this.current);
      this.current = 1;
    },
    calculateMinMax(value) {
      this.maxRows =
        value * this.maxItems <= this.sizeArray
          ? value * this.maxItems
          : this.sizeArray;
      this.minRows =
        value * this.maxItems <= this.sizeArray
          ? this.maxRows - this.maxItems + 1
          : (this.current - 1) * this.maxItems + 1;
    },
    goTo(page) {
      if (page === "...") return;

      if (!page || typeof page.target === "undefined") {
        this.current = page;
        this.go = page;
        return;
      }

      const value = parseInt(page.target.value, 10);
      this.go = value < 1 ? 1 : value <= this.total ? value : this.total;
      this.current = this.go;
    },
    getPages() {
      if (this.total <= this.max) {
        this.pages = this.setPages(1, this.total);
      }

      const even = this.max % 2 === 0 ? 1 : 0;
      this.prevRange =
        this.total < 6
          ? Math.floor(this.max / (this.max / 2))
          : Math.floor(this.max / 2);
      this.nextRange = this.total - this.prevRange + 1 + even;

      if (this.current >= this.prevRange && this.current <= this.nextRange) {
        const start = this.current - this.prevRange + 2;
        const end = this.current + this.prevRange - 2 - even;
        this.pages = [
          1,
          "...",
          ...this.setPages(start, end),
          "...",
          this.total,
        ];
      } else if (this.total < 6) {
        this.pages = this.setPages(1, this.total);
      } else {
        this.pages = [
          ...this.setPages(1, this.prevRange),
          "...",
          ...this.setPages(this.nextRange, this.total),
        ];
      }
    },
    setPages(start, end) {
      const pages = [];
      for (let page = start > 0 ? start : 1; page <= end; page += 1) {
        pages.push(page);
      }
      return pages;
    },
    nextPage() {
      if (this.current < this.total) this.current += 1;
    },
    prevPage() {
      if (this.current > 1) this.current -= 1;
    },
  },
};
</script>
